import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Today } from "@ca/shared";
import { Ring } from "@ca/ui";
import { api } from "./api.ts";
import { type StudyStatus } from "./study-tracker.ts";
export function useToday(uid: string) {
  const cache = useQueryClient();
  const q = useQuery({
    queryKey: ["today", uid],
    queryFn: async () => Today.parse(await api("/v1/me/today", { accountId: uid })),
    refetchInterval: 30000,
  });
  useEffect(() => {
    const refresh = () => {
      void cache.invalidateQueries({ queryKey: ["today", uid] });
      void cache.invalidateQueries({ queryKey: ["activity", uid] });
    };
    window.addEventListener("ca-study-ack", refresh);
    return () => {
      window.removeEventListener("ca-study-ack", refresh);
    };
  }, [uid, cache]);
  return q;
}
const RULE_TIP =
  "Chỉ tính khi bạn đã vào làm: trong đề, phiên luyện part, phiên ôn thẻ, chép chính tả, bài trị lỗi… Bấm vào menu hay xem danh sách thì không tính.";
/** Viên giờ học `.tpill` trên thanh trên cùng: vòng mini + phút hôm nay + chấm trạng thái ghi giờ. */
export function StudyIndicator({ accountId }: { accountId: string }) {
  const q = useToday(accountId),
    [status, setStatus] = useState<StudyStatus | null>(null);
  useEffect(() => {
    const update = (e: Event) => {
      setStatus((e as CustomEvent<StudyStatus>).detail);
    };
    window.addEventListener("ca-study-status", update);
    return () => {
      window.removeEventListener("ca-study-status", update);
    };
  }, []);
  const goal = q.data?.goalMinutes ?? 60,
    minutes = Math.floor((q.data?.activeSecs ?? 0) / 60),
    live = !status ? "none" : status.state === "recording" ? "on" : "paused";
  return (
    <Link
      to="/$"
      params={{ _splat: "report" }}
      className="tpill"
      aria-label="Hôm nay đã học, mở báo cáo giờ học"
    >
      <span className="mini-ring">
        <Ring pct={(minutes / goal) * 100} sw={14} color="var(--accent-2)" />
      </span>
      <span className="tp-txt">
        {minutes}/{goal}
        <span className="tp-unit"> phút</span>
      </span>
      <span
        className={`live ${live}`}
        data-tip={
          live === "on"
            ? `Đang tính giờ học|${RULE_TIP}`
            : live === "paused"
              ? `Đã tạm dừng tính giờ|${status?.message ?? ""}`
              : `Chưa tính giờ|${RULE_TIP}`
        }
      >
        <i />
        <span className="lv-lbl">
          {live === "on" ? "· đang tính giờ" : live === "paused" ? "· tạm dừng" : "chưa tính giờ"}
        </span>
      </span>
    </Link>
  );
}
/** Chip `.sesschip` trên thanh màn làm bài: trạng thái ghi giờ học của bài đang mở. */
export function StudyNotice() {
  const [status, setStatus] = useState<StudyStatus | null>(null);
  useEffect(() => {
    const update = (event: Event) => {
      setStatus((event as CustomEvent<StudyStatus>).detail);
    };
    window.addEventListener("ca-study-status", update);
    return () => {
      window.removeEventListener("ca-study-status", update);
    };
  }, []);
  const on = status?.state === "recording";
  const problem = status && !["recording", "paused"].includes(status.state);
  return (
    <span
      className={`sesschip${on ? "" : " paused"}`}
      role="status"
      data-tip={`Giờ học của bài này|Bắt đầu tính khi bạn vào bài, dừng khi bạn thoát ra. Tạm dừng khi rời tab hoặc 3 phút không thao tác.${problem ? `|${status.message}` : ""}`}
    >
      <span className="ss-dot" />
      <span className="ss-lbl">
        {on
          ? "đang tính giờ học"
          : problem
            ? `${status.message}${status.state === "revoked" ? " · mở lại bài ở tab này để ghi giờ" : ""}`
            : "tạm dừng tính giờ"}
      </span>
    </span>
  );
}
